import React, { Fragment } from 'react';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { metaPropTypes } from 'config/propTypes';
import statePageContent from 'config/statePageContent';
import stateSelector from 'store/selectors/state';
import { withErrorHandler, withRouteHandler } from 'decorators';
import { Page } from 'components/core/Page';
import { StateHero } from 'components/organisms/StateHero';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import StatePageSkeleton from './StatePageSkeleton';

import './StatePage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import CityCardList from 'components/organisms/CityCardList/CityCardList';
import BreadcrumbsBar from 'components/organisms/BreadcrumbsBar/BreadcrumbsBar';
import { Helmet } from 'react-helmet';

export const enhance = compose(
	connect(stateSelector),
	withErrorHandler,
	withRouteHandler((props) => !props.title && !props.isLoading),
	withProps(({ location }) => {
		// Guard against null location during loading state
		if (!location || !location.state) {
			return { content: null };
		}
		const { state } = location; // overriding the actual state, which would be location.state
		return { content: statePageContent[state] };
	}),
	withPageViewTracking(PAGE_TYPES.STATE_LANDING),
);

export function StatePage({
	trackingPixel,
	meta,
	location,
	stateUrl,
	drilldownLinks,
	isLoading,
}) {
	// Show skeleton during loading to prevent CLS (Cumulative Layout Shift)
	// The skeleton reserves the same space as the actual content,
	// preventing the footer from jumping to the top during navigation
	if (isLoading || !location) {
		return <StatePageSkeleton />;
	}

	const websiteSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'SelfStorage',
		url: 'https://www.selfstorage.com/',
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: `https://www.selfstorage.com/search?location=${location?.fullState}%2C%20USA`,
				inLanguage: 'en-US',
				actionPlatform: [
					'https://schema.org/DesktopWebPlatform',
					'https://schema.org/MobileWebPlatform',
				],
			},
			'query-input': {
				'@type': 'PropertyValueSpecification',
				valueRequired: 'https://schema.org/True',
				valueName: 'search_term_string',
				valuePattern: location?.fullState || '',
			},
		},
	};

	const webPageSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		name: `${location.fullState} Self Storage | Find Self Storage Options by State`,
		url: (meta && meta.canonical) || '',
		description: `Explore the most affordable self-storage options in ${location?.fullState}. Quickly locate facilities in your city and reserve the perfect unit online in minutes.`,
		mainEntity: {
			'@type': 'Place',
			name: location?.fullState,
		},
	};

	return (
		<Page
			className="state-landing-page"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.STATE_LANDING}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
		>
			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify(websiteSchema)}
				</script>
				<script type="application/ld+json">
					{JSON.stringify(webPageSchema)}
				</script>
			</Helmet>
			<BreadcrumbsBar
				location={location}
				stateUrl={stateUrl}
			/>
			<StateHero
				location={location}
				stateUrl={stateUrl}
				stateNickname={statePageContent[location.state].stateNickname}
			/>

			{!get(statePageContent[location.state], 'isDC') && (
				<Fragment>
					{drilldownLinks && drilldownLinks.length > 0 && (
						<CityCardList drilldownLinks={drilldownLinks} />
					)}
				</Fragment>
			)}
		</Page>
	);
}

StatePage.propTypes = {
	drilldownLinks: PropTypes.arrayOf(
		PropTypes.shape({
			city: PropTypes.string,
			state: PropTypes.string,
			content: PropTypes.shape({
				unitTypes: PropTypes.array,
				vehicleStorage: PropTypes.array,
				amenities: PropTypes.array,
			}),
		}),
	),
	location: PropTypes.shape({
		state: PropTypes.string.isRequired,
		fullState: PropTypes.string.isRequired,
	}),
	stateUrl: PropTypes.string,
	trackingPixel: PropTypes.string,
	meta: PropTypes.shape(metaPropTypes),
	isLoading: PropTypes.bool,
};

StatePage.defaultProps = {
	location: null,
	stateUrl: '',
	trackingPixel: null,
	meta: {},
	isLoading: false,
};

export default enhance(StatePage);
